/* eslint-disable no-param-reassign */
import { connect, ConnectedProps } from 'react-redux';
import React from 'react';
import { withInject } from '@piwikpro/platform';
import { Notifications } from '@piwikpro/notification-crate';
import {
  Card,
  Icon,
  Stack,
  Text,
  Button,
  CodeSnippet,
  withTranslation,
} from '@piwikpro/ui-components';
import { IAuthSettingsState } from '@piwikpro/auth-crate/reducers/auth';

const connector = connect(
  (state: { authSettings: IAuthSettingsState }) => ({
    data: state.authSettings.user.setup.data,
  }),
);

interface InjectedProps {
  notifications: Notifications
}

type ReduxProps = ConnectedProps<typeof connector>;

interface Props extends ReduxProps, InjectedProps {
  onClose: ({ setupCompleted }: { setupCompleted: boolean }) => void
  t: (key: string) => string
}

export const TwoFactorBackupCodes = ({
  data,
  notifications,
  onClose,
  t,
}: Props) => {
  // eslint-disable-next-line no-return-assign
  const backupCodes = data?.backup_codes!.reduce((prev, curr, currIndex) => (
    (currIndex === data.backup_codes!.length - 1)
      ? prev += `${curr}`
      : prev += `${curr}\n`
  ), '');

  const handleCopyToClipboard = (result: boolean) => {
    if (result) {
      notifications.push({
        text: 'components:notifications.copied.success',
        type: 'success',
      });
    } else {
      notifications.push({
        text: 'components:notifications.copied.error',
        type: 'error',
      });
    }
  };

  return (
    <>
      <Card.Section>
        <Stack vertical horizontalAlignment="middle">
          <Stack.Item>
            <Icon size="large" color="green" name="checkmark-circle" />
          </Stack.Item>
          <Stack.Item>
            <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">{t('security.2fa.configure.backupCodes.title')}</Text>
          </Stack.Item>
          <Stack.Item previousSpacing="narrow">
            <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">{t('security.2fa.configure.backupCodes.subtitle')}</Text>
          </Stack.Item>
        </Stack>
      </Card.Section>
      <Card.Section>
        <Stack vertical horizontalAlignment="middle">
          <Stack.Item>
            <Text as="p">
              {t('security.2fa.configure.backupCodes.description')}
            </Text>
          </Stack.Item>
          <Stack.Item fill>
            <CodeSnippet
              code={backupCodes}
              onCopy={handleCopyToClipboard}
              mode="no-highlight"
            />
          </Stack.Item>
          <Stack.Item fill>
            <Button
              appearance="primary"
              text={t('security.2fa.configure.backupCodes.doneButtonText')}
              onClick={() => onClose({ setupCompleted: true })}
              fullWidth
            />
          </Stack.Item>
        </Stack>
      </Card.Section>
    </>
  );
};

export default connector(withInject<InjectedProps>({
  notifications: 'NotificationCrate.notifications',
})(withTranslation(['profile', 'administration'])(TwoFactorBackupCodes)));
